import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { from, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Auth } from './auth';
import { BillingService } from '../billing.service';
import { CardRequiredService } from '../card-required.service';

/** Auth endpoints that must NOT receive a stale Bearer token (backend rejects invalid tokens with 401 even on AllowAny). */
const AUTH_ENDPOINTS = [
  '/identity/auth/login/',
  '/identity/auth/signup/',
  '/identity/auth/forgot-password/',
  '/identity/auth/reset-password-confirm/',
  '/identity/auth/confirm-email/',
  '/identity/auth/invite/',
];

/** Public (token-based) endpoints. JWTAuthentication is still wired in DRF defaults,
 * so an expired/stale Bearer token here will short-circuit AllowAny views with a 401.
 * External reviewers (EPO/Warranty/Order share links) must reach these with no
 * Authorization header regardless of what's in localStorage. */
const PUBLIC_SHARE_ENDPOINTS = [
  '/projects/public/share/',
  '/projects/public/order/share/',
];

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some(path => url.includes(path));
}

function isPublicShareEndpoint(url: string): boolean {
  return PUBLIC_SHARE_ENDPOINTS.some(path => url.includes(path));
}

function isSkippableProtectedIdentityEndpoint(url: string): boolean {
  return url.includes('/identity/status/')
    || /\/identity\/orgs\/[^/]+\/members\//.test(url);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(Auth);
  const router = inject(Router);
  const billing = inject(BillingService);
  const cardRequiredSvc = inject(CardRequiredService);
  const toastr = inject(ToastrService);

  const access = auth.getAccessToken();
  const selectedProfileId = auth.getSelectedProfileId();
  
  let headers: { [key: string]: string } = {};
  
  // Do NOT attach stale tokens to auth endpoints (login, signup) or to public share
  // endpoints (EPO/Warranty/Order links). DRF's JWTAuthentication rejects invalid/
  // expired tokens with 401 before AllowAny views run — that's the source of the
  // "external reviewer sees a 401" problem.
  const url = req.url ?? '';
  const skipToken = isAuthEndpoint(url) || isPublicShareEndpoint(url);
  if (!access && isSkippableProtectedIdentityEndpoint(url)) {
    return throwError(() => new HttpErrorResponse({
      status: 0,
      statusText: 'Skipped',
      url,
      error: { detail: 'Skipped protected identity request without an access token.' },
    }));
  }
  if (access && !skipToken) {
    headers['Authorization'] = `Bearer ${access}`;
  }

  // Same rule for org/profile headers — public share links must reach the backend
  // with no organizational context so the response is identical for anyone.
  if (selectedProfileId && !isPublicShareEndpoint(url)) {
    headers['X-Profile-ID'] = selectedProfileId.toString();
    const profiles = auth.getProfiles();
    const selectedProfile = profiles.find(p => p.id === selectedProfileId);
    if (selectedProfile?.org_slug) {
      headers['X-Org'] = selectedProfile.org_slug;
    }
  }
  
  const authReq = Object.keys(headers).length > 0
    ? req.clone({ setHeaders: headers })
    : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Don't redirect when on share page, auth pages, legal pages (privacy, terms, cookies), or public share API
        const path = (typeof window !== 'undefined' && window.location?.pathname) || router.url || '';
        const isOnSharePage = path.startsWith('/share/');
        const isOnAuthPage = path.startsWith('/auth/');
        const isOnLegalPage = path.startsWith('/legal/');
        const isPublicShareApi = isPublicShareEndpoint(req.url ?? '');
        if (!isOnSharePage && !isOnAuthPage && !isOnLegalPage && !isPublicShareApi) {
          auth.logout();
          router.navigate(['/auth/login']);
        }
      }
      if (error instanceof HttpErrorResponse && error.status === 402) {
        const body = error.error as { error?: string; billing_lock_state?: string; days_overdue?: number; trial_ends_at?: string | null } | null;
        const path = (typeof window !== 'undefined' && window.location?.pathname) || router.url || '';
        // The billing admin page stays reachable while locked (its APIs are
        // allowlisted); a stray 402 from an unrelated background call must
        // not yank an admin out of paying / adding a card.
        const exempt = path.startsWith('/suspended')
          || path.startsWith('/dashboard/admin/billing')
          || path.startsWith('/auth/')
          || path.startsWith('/share/')
          || path.startsWith('/legal/');

        if (body?.error === 'billing_hard_locked' || body?.billing_lock_state === 'hard_lock') {
          // Hard-lock 402 from billing.hardlock.BillingHardLockPermission —
          // an OVERDUE INVOICE, the only condition that's ever account-wide.
          // Leave the workspace immediately: navigate to the dedicated
          // /suspended page instead of overlaying already-loaded data.
          billing.noteHardLockFromError(body?.days_overdue);
          if (!exempt) {
            router.navigate(['/suspended']);
          }
        } else if (body?.error === 'card_required') {
          // billing.hardlock.CardRequiredForLiveProjectWrites (owner ruling
          // 2026-08-18): a WRITE against a non-draft project with no card on
          // file — NEVER an account-wide lock, NEVER a /suspended redirect.
          // The start-project modal already has its own inline card step
          // (project-detail.ts) predating this change — leave that path
          // alone rather than double-handle it here. Every OTHER card_required
          // 402 (order submit, EPO/warranty create+submit, task-work writes,
          // project edits, ...) is handled the same way, for every screen,
          // from this one place: a billing admin gets the add-card modal and
          // the original request is retried once card is added; anyone else
          // (most commonly a subcontractor recording their own work — they
          // can't add the org's card) gets a clear, non-actionable message.
          const isStartProjectCall = /\/projects\/projects\/[^/]+\/start-project\/?(\?|$)/.test(
            req.url ?? ''
          );
          if (!isStartProjectCall) {
            if (billing.canManageBilling()) {
              return from(cardRequiredSvc.promptForCard(body?.trial_ends_at ?? null)).pipe(
                switchMap(added => added ? next(authReq) : throwError(() => error))
              );
            }
            toastr.error('Your contractor needs to add a payment method before work can be recorded.');
          }
        }
      }
      return throwError(() => error);
    })
  );
};
